import React, { lazy, useMemo } from 'react';
import { connect } from 'react-redux';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import useTheme from 'theme/useTheme';
import util from 'utils/utils';

import Entry from 'components/entry/Entry';
import PublicChallenges from 'components/challenges/PublicChallenges';
import ProjectBoard from 'components/boards';
import NewProjectBoard from 'components/boards/New';
import MobileMessageWidget from 'components/messages/MobileMessageWidget';
import MobileNotificationsWidget from 'components/notifications/MobileNotificationsWidget';
import Home from 'components/home/Home';
import Challenges from 'components/challenges/Challenges';
import Groups from 'components/groups/Groups';
import NewGroup from 'components/groups/New';
import Tags from 'components/tags';
import UserProfile from 'components/users/Profile';
import UserUnsubscribe from 'components/users/Unsubscribe';
import MessageCentre from 'components/messages/MessageCentre';
import Preferences from 'components/users/Settings';
import UserInvitations from 'components/users/Invitations'
import OAuthConnection from 'components/entry/OAuthConnection';
import Idea from 'components/ideas';
import Challenge from 'components/challenges/Challenge';
import NotFound from 'components/NotFound';
import PersistentToken from 'components/app/PersistentToken';
import CustomPage from './OrganisationCustomPage';

// Code-split routes. Note this does not work properly on some networks (e.g. Betsi Cadwaladr Healthboard) so
// we only lazy-load a few routes.
const Group = lazy(() => import('components/groups/Group'));
const SuperAdministration = lazy(() => import('components/superadmin/SuperAdministration'));
const Administration = lazy(() => import('components/admin/Administration'));
const ChallengeSettings = lazy(() => import('components/challenges/Challenge/Settings'));
const InnovationIntelligence = lazy(() => import('components/innovationintelligence/InnovationIntelligence'));
const Achievements = lazy(() => import('components/users/Achievements'));
const ChallengeProfile = lazy(() => import('components/profile/ChallengeProfile'));
const YourIdeas = lazy(() => import('components/challenges/YourIdeas'));
const GenAIChallengeWizard = lazy(() => import('components/challenges/GenAIChallengeWizard'));

const UserSwitch = ({
  user,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const canAccessInnovationIntelligence = useMemo(() => user && util.organisationFeaturesEnabled(user, ['innovationIntelligence']) && (util.isInRole(user, 'super.viewDashboard') || util.isInRole(user, 'org.innovationIntelligence', user?.organisation?._id)), [user]);

  if (location.pathname !== '/invitations' && params.get('invite_token')) {
    return <Redirect to={`/invitations?invite_token=${params.get('invite_token')}`} />;
  }

  if (!user) return null;
  return (
    <Switch>
      <Redirect from='/challenges/:id/format' to='/challenges/:id/settings/format' />
      <Route exact path='/' component={Home} />
      <Route path="/custom-pages/:pageId" component={CustomPage} />
      <Route path='/unsubscribe' component={UserUnsubscribe} />
      <Route path='/invitations' component={UserInvitations} />
      <Route path='/achievements' component={Achievements} />
      {(util.organisationFeaturesEnabled(user, ['openAI']) || util.isInRole(user, 'super.viewDashboard')) && <Route path='/challenges/new/genai' component={GenAIChallengeWizard} />}
≈      <Route path='/challenges/public' component={PublicChallenges} />
      <Route exact path='/challenges/discover' component={Challenges} />
      <Route path='/challenges/:id/settings' component={ChallengeSettings} />
      <Route path='/challenges/:id' component={Challenge} />
      <Route path='/boards/new' component={NewProjectBoard} />
      <Route path='/boards/:id' component={ProjectBoard} />
      <Route exact path='/challenges' component={Challenges} />
      <Route exact path='/ideas' component={YourIdeas} />
      <Route path='/profiles/:id' component={ChallengeProfile} />
      <Route path='/ideas/:id' component={Idea} />
      <Route exact path='/groups/discover' component={Groups} />
      <Route exact path='/groups' component={Groups} />
      <Route path='/groups/new' component={NewGroup} />
      <Route path='/groups/:id' component={Group} />
      <Route exact path='/tags' component={Tags} />
      <Route path='/tags/:ids+' component={Tags} />
      <Route path='/users/:id' component={UserProfile} />
      <Route path='/messages' component={user?.ghostingAs ? Home : MessageCentre} />
      {canAccessInnovationIntelligence && <Route path='/innovationintelligence' component={InnovationIntelligence} />}
      {theme.sizes.isMobile && <Route path='/messagetray' component={user?.ghostingAs ? Home : MobileMessageWidget} />}
      {theme.sizes.isMobile && <Route path='/notifications' component={MobileNotificationsWidget} />}
      <Route path='/preferences' component={Preferences} />
      <Route path='/account' component={Preferences} />
      {util.isInRole(user, 'org.viewDashboard', user.organisation._id) && <Route path='/admin' component={Administration} />}
      {util.isInRole(user, 'super.viewDashboard') && <Route path='/superadmin' component={SuperAdministration} />}
      <Route path='/oauth/connections/:type' component={OAuthConnection} />
      <Route path='/login' component={Entry} />
      <Route path='/register' component={Entry} />
      <Route path="/join/:token" component={PersistentToken} />
      <Route component={NotFound} />
    </Switch>
  );
};

const mapStateToProps = ({ user }) => ({ user });

export default connect(mapStateToProps)(UserSwitch);
